import { Button } from '@mui/material';
import { CreatePlayerDto, PlayerDto, UpdatePlayerDto } from 'inside-fifa/api/players/dto';
import { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { PlayerFormValues } from './partials/player-form-dialog/dto';
import { createPlayer } from 'inside-fifa/api/players/create-player';
import { deletePlayer } from 'inside-fifa/api/players/delete-player';
import { updatePlayer } from 'inside-fifa/api/players/update-player';
import { getPlayers } from 'inside-fifa/api/players/get-players';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { AxiosError } from 'axios';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import useAuth from 'hooks/useAuth';
import { isUserInAllowedGroup } from 'utils/route-guard/roles-check';
import { INSIDE_FIFA_TEAM_ADMIN } from 'inside-fifa/setup/auth';

const usePlayersManagement = () => {
    const [players, setPlayers] = useState<Array<PlayerDto>>([]);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const fetchPlayers: BaseTableFetchFuncProps = useCallback(async ({ filters, pageIndex, pageSize, searchTerm }) => {
        try {
            const response = await getPlayers({
                pageIndex,
                pageSize,
                andSpecifications: filters ? mapFiltersToSpecification(filters) : undefined,
                orSpecifications:
                    searchTerm && searchTerm.length > 0
                        ? mapSearchParamToSpecification(searchTerm, ['firstName', 'lastName'])
                        : undefined,
            });

            setPlayers(response.elements);
            setPageCount(Math.ceil(response.total / pageSize));
            setCurrentPage(pageIndex);
        } catch {
            setPlayers([]);
            setPageCount(0);
        }
    }, []);

    const [isCreatingNewPlayer, setIsCreatingNewPlayer] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState<PlayerDto | undefined>(undefined);

    const onAddPlayerClick = useCallback(() => setIsCreatingNewPlayer(true), []);

    const onCloseDialogClick = useCallback(() => {
        setIsCreatingNewPlayer(false);
        setSelectedPlayer(undefined);
    }, []);

    const onSelectPlayer = useCallback((player: PlayerDto) => setSelectedPlayer(player), []);
    const createNewPlayer = useCallback(
        async (data: CreatePlayerDto) => {
            const newPlayer = await createPlayer(data);

            if (currentPage + 1 === pageCount) {
                setPlayers((prev) => [...prev, newPlayer]);
            }

            onCloseDialogClick();
        },
        [currentPage, onCloseDialogClick, pageCount],
    );

    const updateExistingPlayer = useCallback(
        async (data: UpdatePlayerDto) => {
            try {
                const updatedPlayer = await updatePlayer(data);

                setPlayers((prev) =>
                    prev.map((player) => {
                        if (player.id === updatedPlayer.id) {
                            return updatedPlayer;
                        }

                        return player;
                    }),
                );
                onCloseDialogClick();
            } catch (error) {
                const typedError = error as AxiosError;
                let message = 'Something weird happen, please contact the Inside FIFA Team.';

                if (typedError.status === 409) {
                    message = 'Player has been updated by other User. Please reload the page to refresh the data.';
                }

                dispatch(
                    openSnackbar({
                        open: true,
                        message,
                        variant: 'alert',
                        alert: {
                            color: 'error',
                        },
                    }),
                );
            }
        },
        [onCloseDialogClick],
    );

    const onSubmit = useCallback(
        async (data: PlayerFormValues) => {
            if (selectedPlayer) {
                await updateExistingPlayer({
                    id: selectedPlayer.id,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    jerseyNumber: data.jerseyNumber,
                    modifiedTimestamp: selectedPlayer.modifiedTimestamp,
                });
            } else {
                await createNewPlayer({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    jerseyNumber: data.jerseyNumber,
                });
            }
        },
        [createNewPlayer, selectedPlayer, updateExistingPlayer],
    );

    const onDeletePlayer = useCallback(async (playerToDelete: PlayerDto) => {
        await deletePlayer(playerToDelete);
        setPlayers((prev) => prev.filter((player) => player.id !== playerToDelete.id));
    }, []);

    return {
        players,
        pageCount,
        fetchPlayers,
        isDialogOpen: !!selectedPlayer || isCreatingNewPlayer,
        editMode: !!selectedPlayer,
        selectedPlayer,
        onAddPlayerClick,
        onCloseDialogClick,
        onSelectPlayer,
        onSubmit,
        onDeletePlayer,
    };
};

type UsePlayersListColumnsConfigParams = {
    onEditPlayer: (player: PlayerDto) => void;
    onDeletePlayer: (player: PlayerDto) => void;
};

const usePlayersListColumnsConfig = ({ onEditPlayer, onDeletePlayer }: UsePlayersListColumnsConfigParams) => {
    const { user } = useAuth();

    const columns = useMemo<Column[]>(() => {
        const cols: Column[] = [
            {
                Header: 'Player Id',
                accessor: 'id',
            },
            {
                Header: 'First Name',
                accessor: 'firstName',
            },
            {
                Header: 'Last name',
                accessor: 'lastName',
            },
            {
                Header: 'Jersey Number',
                accessor: 'jerseyNumber',
            },
        ];

        if (isUserInAllowedGroup(user?.groups ?? [], [INSIDE_FIFA_TEAM_ADMIN])) {
            cols.push({
                Header: 'Actions',
                disableFilters: true,
                Cell: ({ row }) => {
                    const player = row.original as PlayerDto;

                    if (!player) {
                        return <></>;
                    }

                    return (
                        <>
                            <Button
                                onClick={() => {
                                    onEditPlayer(player);
                                }}
                            >
                                Edit
                            </Button>
                            <Button
                                onClick={() => {
                                    onDeletePlayer(player);
                                }}
                            >
                                Delete
                            </Button>
                        </>
                    );
                },
            });
        }

        return cols;
    }, [onDeletePlayer, onEditPlayer, user?.groups]);

    const defaultVisibleColumns = useMemo(() => ['firstName', 'lastName', 'jerseyNumber'], []);

    return { columns, defaultVisibleColumns };
};

export { usePlayersManagement, usePlayersListColumnsConfig };
